import React from 'react';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileSaver from 'file-saver';
import { CopyIconButton } from 'new/elements/Button/CopyIconButton';
import { PayloadLanguage } from './payloadBlocks';
import s from './PayloadCodeBlock.module.scss';

export interface PayloadCodeBlockProps {
  language: PayloadLanguage;
  content: string;
}

const MIME_TYPE: Record<PayloadLanguage, string> = {
  xml: 'application/xml;charset=utf-8',
  json: 'application/json;charset=utf-8',
};

/** Boxed code block for XML/JSON payloads the triage agent quotes verbatim
 *  (SOAP bodies, log lines): a labeled header plus copy/download quick
 *  actions, matching the "code block with a language chip" pattern most chat
 *  UIs use. Swapped in for the plain `<pre>` markdown-to-jsx would otherwise
 *  render, via the `pre` override in `markdownOptions.tsx`. */
export const PayloadCodeBlock: React.FC<PayloadCodeBlockProps> = ({ language, content }) => {
  const download = () => {
    const blob = new Blob([content], { type: MIME_TYPE[language] });
    FileSaver.saveAs(blob, `triage-payload.${language}`);
  };

  return (
    <div className={s.block} data-testid="triage-payload-block">
      <div className={s.header}>
        <span className={s.label} data-testid="triage-payload-language">
          {language.toUpperCase()}
        </span>
        <div className={s.actions}>
          <CopyIconButton text={content} className={s.actionButton} title="Copy" />
          <button
            type="button"
            className={s.actionButton}
            onClick={download}
            title="Download"
            aria-label="Download"
            data-testid="triage-payload-download"
          >
            <FileDownloadOutlinedIcon fontSize="inherit" />
          </button>
        </div>
      </div>
      <pre className={s.pre}>
        <code>{content}</code>
      </pre>
    </div>
  );
};
PayloadCodeBlock.displayName = 'PayloadCodeBlock';
